import { Capacitor } from '@capacitor/core';

// Check if running in native platform
export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
export const isIOS = isNative && platform === 'ios';

// Initialize native features
export const initNative = async () => {
  if (!isNative) return;

  try {
    // Dynamic imports to avoid issues on web
    const { SplashScreen } = await import('@capacitor/splash-screen');
    const { App } = await import('@capacitor/app');

    // Hide splash after app loads
    await SplashScreen.hide();

    // Status bar appearance handled natively:
    // - Android: MainActivity.java setupSystemBarsInsets() applies system bar padding
    // - iOS: viewport-fit=cover + env(safe-area-inset-top) CSS handles status bar inset
    // Note: StatusBar.setOverlaysWebView is unimplemented on iOS in Capacitor 6

    // Handle back button on Android
    // Priority: 1) Open modals (via modalManager) 2) BottomNav navigation 3) history.back / exit
    const { hasOpenModals } = await import('../utils/modalManager');
    App.addListener('backButton', () => {
      // If any modal/bottom sheet/select is open, let popstate close it
      if (hasOpenModals()) {
        window.history.back();
        return;
      }

      // Dispatch to BottomNav for tab navigation / exit handling
      const event = new CustomEvent('app-back-button');
      const handled = !window.dispatchEvent(event); // preventDefault = handled
      if (!handled) {
        // No handler caught it — use normal back or exit
        if (window.history.length > 1) {
          window.history.back();
        } else {
          App.exitApp();
        }
      }
    });

    // Handle app state changes (for refreshing data when app comes to foreground)
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // App came to foreground - could trigger data refresh here
      }
    });

    // Handle deep links
    App.addListener('appUrlOpen', () => {
      // Handle deep link navigation if needed
    });

  } catch {
    // Silent fail for native initialization
  }
};

// Haptic feedback
export const hapticFeedback = async (style = 'light') => {
  if (!isNative) return;

  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const styles = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy
    };
    await Haptics.impact({ style: styles[style] || ImpactStyle.Light });
  } catch {
    // Silent fail for haptics
  }
};

// Keyboard handling
export const hideKeyboard = async () => {
  if (!isNative) {
    // Web fallback
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    return;
  }

  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    await Keyboard.hide();
  } catch {
    // Silent fail for keyboard
  }
};

// Show accessory bar (iOS only) - shows Done/Next buttons above keyboard
export const setAccessoryBarVisible = async (visible = true) => {
  if (!isNative || platform !== 'ios') return;

  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    await Keyboard.setAccessoryBarVisible({ isVisible: visible });
  } catch {
    // Silent fail
  }
};

// Set keyboard scroll behavior
export const setKeyboardScroll = async (enabled = true) => {
  if (!isNative) return;

  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    await Keyboard.setScroll({ isDisabled: !enabled });
  } catch {
    // Silent fail
  }
};

// Get app info
export const getAppInfo = async () => {
  if (!isNative) return null;

  try {
    const { App } = await import('@capacitor/app');
    return await App.getInfo();
  } catch {
    return null;
  }
};

// Exit app (Android only)
export const exitApp = async () => {
  if (!isNative || platform !== 'android') return;

  try {
    const { App } = await import('@capacitor/app');
    await App.exitApp();
  } catch {
    // Silent fail for exit
  }
};


// Open app settings in iOS/Android Settings
export const openAppSettings = async () => {
  if (!isNative) return;

  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
    await NativeSettings.open({
      optionAndroid: AndroidSettings.ApplicationDetails,
      optionIOS: IOSSettings.App
    });
  } catch {
    // Fallback to iOS-only URL scheme if plugin fails
    try {
      const { App } = await import('@capacitor/app');
      await App.openUrl({ url: 'app-settings:' });
    } catch {
      // Silent fail
    }
  }
};

// Show native permission denied dialog with Cancel/Settings buttons (like WhatsApp)
// Returns true if user tapped Settings, false if cancelled
export const showPermissionDeniedDialog = async (feature) => {
  if (!isNative) return false;

  const messages = {
    camera: 'Hisab Journey does not have access to your camera. To enable access, tap Settings and turn on Camera.',
    photos: 'Hisab Journey does not have access to your photos. To enable access, tap Settings and turn on Photos.',
    microphone: 'To use voice input, Hisab Journey needs access to your microphone. Tap Settings and turn on Microphone.',
    notifications: 'Hisab Journey needs notifications to alert you when your file is ready. Tap Settings and turn on Notifications.',
  };

  try {
    const { Dialog } = await import('@capacitor/dialog');
    const { value } = await Dialog.confirm({
      title: '',
      message: messages[feature] || 'Permission required. Tap Settings to enable.',
      okButtonTitle: 'Settings',
      cancelButtonTitle: 'Cancel'
    });

    if (value) {
      await openAppSettings();
    }
    return value;
  } catch {
    // Fallback to browser confirm if Dialog plugin fails
    const goToSettings = confirm(messages[feature] || 'Permission required. Tap OK to open Settings.');
    if (goToSettings) {
      await openAppSettings();
    }
    return goToSettings;
  }
};

/**
 * Check and request photo/camera permissions for file uploads on native.
 * Returns true if permission granted, false if denied (shows Settings dialog).
 * On web, always returns true (browser handles permissions).
 */
export const checkFileUploadPermissions = async () => {
  if (!isNative) return true;

  try {
    const { Camera: CapCamera } = await import('@capacitor/camera');
    const perms = await CapCamera.checkPermissions();

    // Check photos permission
    if (perms.photos === 'denied') {
      await showPermissionDeniedDialog('photos');
      return false;
    }
    if (perms.photos === 'prompt' || perms.photos === 'prompt-with-rationale') {
      const requested = await CapCamera.requestPermissions({ permissions: ['photos'] });
      if (requested.photos === 'denied') {
        await showPermissionDeniedDialog('photos');
        return false;
      }
    }

    // Check camera permission (for "Take Photo" option in file picker)
    if (perms.camera === 'denied') {
      // Camera denied but photos allowed — still let user pick from gallery
      // Don't block, just skip camera check
    } else if (perms.camera === 'prompt' || perms.camera === 'prompt-with-rationale') {
      // Request camera permission proactively so "Take Photo" works in file picker
      await CapCamera.requestPermissions({ permissions: ['camera'] });
      // Don't block on denial — user can still pick from gallery
    }

    return true;
  } catch {
    // Plugin not available, let the web file picker handle it
    return true;
  }
};

// Open URL in external browser (works for native apps, PWA, and web)
export const openExternalUrl = (url) => {
  // For all platforms, use window.open with _system target for native
  // and _blank for web/PWA to ensure it opens in external browser
  if (isNative) {
    // On native Capacitor apps, _system opens in the system browser
    // instead of the in-app WebView
    window.open(url, '_system');
  } else {
    // For web/PWA, use _blank with security attributes
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};
